import { CustomTextField } from 'components/forms';
import { useGetDocTypes } from 'core/document-types';
import { AxiosCustomError } from 'core/errors';
import { getIn } from 'formik';
import { TextFieldProps } from 'formik-material-ui';
// eslint-disable-next-line
import React, { useEffect, useMemo } from 'react';
import dictionary from 'utils/dictionary';
import { RGX_ONLY_NUMBERS } from 'utils/helpers';
import { SellerInfo } from './entity.model';
import { useCheckDocumentNumber } from './hooks';

type Props = {
  entityId: string;
  selectedEntity: string;
  onLoadData?: (sellerInfo: SellerInfo) => void;
};

const AgentDocumentNumberField: React.FC<Props & TextFieldProps> = ({
  label = dictionary.AgentDocumentNumberField_label,
  inputProps,
  entityId,
  selectedEntity,
  onLoadData,
  ...props
}) => {
  const fieldName = props.field.name;
  const touched = !!getIn(props.form.touched, fieldName);
  const { value } = props.field;
  const { setFieldError, setFieldTouched, setStatus, setErrors } = props.form;

  const minLengthAllowed = 7;

  const entitiesParams = {
    entity_id: entityId,
    document_number: value,
  };

  const {
    data: docTypesData,
    error: docTypesError,
    isError: isDocTypesError,
  } = useGetDocTypes();

  const { isLoading: isLoadingEntities } = useCheckDocumentNumber(
    entitiesParams,
    {
      onSuccess: (data) => {
        if (data?.status !== 'ACTIVO') {
          setFieldError(fieldName, data?.description);
        } else {
          setErrors({});
        }
        if (onLoadData) {
          onLoadData(data);
        }
      },
      onError: (err) => {
        setFieldError(
          fieldName,
          (err as AxiosCustomError).customError.description
        );
      },
    }
  );

  const maxLength = useMemo(() => {
    if (docTypesData && docTypesData.length) {
      return (
        Number(
          docTypesData?.find(({ id }) => id.toUpperCase() === 'CI')?.length
        ) ?? 0
      );
    }
  }, [docTypesData]);

  useEffect(() => {
    setStatus({ isAsyncValidating: isLoadingEntities });
  }, [isLoadingEntities, setStatus]);

  useEffect(() => {
    if (isDocTypesError) {
      setFieldError(
        fieldName,
        (docTypesError as AxiosCustomError)?.customError.description
      );
    }
  }, [docTypesError, fieldName, isDocTypesError, setFieldError]);

  useEffect(() => {
    if (!touched && value.length >= minLengthAllowed) {
      setFieldTouched(fieldName);
    }
  }, [fieldName, setFieldTouched, touched, value.length]);

  useEffect(() => {
    if (selectedEntity !== entityId) {
      props.form.setFieldValue('dni', '');
    }
  }, [entityId, selectedEntity, props.form]);

  return (
    <CustomTextField
      label={label}
      isLoading={isLoadingEntities}
      regex={RGX_ONLY_NUMBERS}
      inputProps={{ ...inputProps, maxLength }}
      {...props}
    />
  );
};

export default AgentDocumentNumberField;
